(() => {
  const normalize = (value) => String(value || '').trim().toLowerCase();

  const updateNavigation = (carousel) => {
    const track = carousel.querySelector('[data-fine-art-track]');
    const previous = carousel.querySelector('[data-fine-art-nav="previous"]');
    const next = carousel.querySelector('[data-fine-art-nav="next"]');
    if (!track || !previous || !next) return;

    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const currentScroll = Math.max(0, Math.min(maxScroll, track.scrollLeft));
    previous.disabled = currentScroll <= 1;
    next.disabled = currentScroll >= maxScroll - 1;
  };

  const initCarousel = (carousel) => {
    if (carousel.dataset.fineArtReady === 'true') return;

    const track = carousel.querySelector('[data-fine-art-track]');
    const previous = carousel.querySelector('[data-fine-art-nav="previous"]');
    const next = carousel.querySelector('[data-fine-art-nav="next"]');
    if (!track || !previous || !next) return;

    const scrollAmount = () => Math.max(track.clientWidth * 0.82, 160);
    previous.addEventListener('click', () => {
      track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
    });
    next.addEventListener('click', () => {
      track.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
    });
    track.addEventListener('scroll', () => updateNavigation(carousel), { passive: true });
    window.addEventListener('resize', () => updateNavigation(carousel), { passive: true });
    carousel.dataset.fineArtReady = 'true';

    window.requestAnimationFrame(() => updateNavigation(carousel));
  };

  const getFieldset = (picker, role) => picker.querySelector(`[data-option-role="${role}"]`);

  const getCheckedValue = (fieldset) => {
    const checked = fieldset && fieldset.querySelector('input[type="radio"]:checked');
    return checked ? checked.value : '';
  };

  const isCanvas = (value) => ['canvas', 'unframed', 'no frame'].includes(normalize(value));
  const isFramed = (value) => normalize(value) === 'framed';

  const setMaterialState = (picker, styleValue, preserveSelection = false) => {
    const fieldset = getFieldset(picker, 'frame-material');
    if (!fieldset) return;

    const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
    const internalInput = inputs.find((input) => input.classList.contains('fine-art-internal-option'));
    const visibleInputs = inputs.filter((input) => input !== internalInput);

    if (isFramed(styleValue)) {
      fieldset.setAttribute('aria-disabled', 'false');
      visibleInputs.forEach((input) => {
        input.disabled = false;
      });
      if (internalInput) {
        internalInput.checked = false;
        internalInput.disabled = true;
      }
      if (!preserveSelection) {
        visibleInputs.forEach((input) => {
          input.checked = false;
        });
      }
      return;
    }

    fieldset.setAttribute('aria-disabled', 'true');
    const checkedVisibleInput = visibleInputs.find((input) => input.checked);
    inputs.forEach((input) => {
      input.checked = false;
      input.disabled = true;
    });

    if (isCanvas(styleValue)) {
      // New catalog data uses a hidden "No frame" value. The first material is
      // a preview-only fallback for older test data until its variants migrate.
      // A restored ?variant= selection keeps its own material so the exact
      // variant resolves.
      const canvasMaterial = internalInput
        || (preserveSelection && checkedVisibleInput)
        || visibleInputs[0];
      if (canvasMaterial) canvasMaterial.checked = true;
    }
  };

  const getVariantData = (picker) => {
    const data = picker.querySelector('script[type="application/json"]');
    if (!data) return [];
    try {
      return JSON.parse(data.textContent);
    } catch (error) {
      return [];
    }
  };

  const getSelectedVariant = (picker, variants) => {
    const fieldsets = Array.from(picker.querySelectorAll('.fine-art-option-fieldset'));
    const selectedOptions = fieldsets.map(getCheckedValue);
    if (selectedOptions.some((value) => !value)) return null;

    return variants.find((variant) => (
      variant.options.length === selectedOptions.length
      && variant.options.every((option, index) => option === selectedOptions[index])
    )) || null;
  };

  const getPriceMap = (picker) => {
    const priceData = document.getElementById(picker.dataset.priceMapId);
    if (!priceData) return {};
    try {
      return JSON.parse(priceData.textContent);
    } catch (error) {
      return {};
    }
  };

  const updatePurchaseState = (picker, variant, priceMap) => {
    const product = picker.closest('.fine-art-product');
    const price = picker.querySelector('[data-fine-art-print-price]');
    const productForm = document.getElementById(`product-form-${picker.dataset.section}`);
    const variantInput = productForm && productForm.querySelector('input[name="id"]');
    const addButton = productForm && productForm.querySelector('[name="add"]');
    const addButtonText = addButton && addButton.querySelector('span');
    const stock = product && product.querySelector('.wbstockcount');
    const quantityControls = product
      ? product.querySelectorAll('.product-form__quantity button, .product-form__quantity input')
      : [];
    const hasAvailableVariant = Boolean(variant && variant.available);

    if (price) {
      const formattedPrice = variant && priceMap[variant.id];
      price.textContent = formattedPrice
        ? `Total ${formattedPrice}`
        : `From ${picker.dataset.startingPrice}`;
    }

    if (variantInput) {
      variantInput.value = variant ? variant.id : '';
      variantInput.disabled = !variant;
    }

    if (addButton) {
      addButton.disabled = !hasAvailableVariant;
      if (addButtonText) {
        addButtonText.textContent = !variant
          ? 'Choose options'
          : hasAvailableVariant
            ? window.variantStrings.addToCart
            : window.variantStrings.soldOut;
      }
    }

    quantityControls.forEach((control) => {
      control.disabled = !hasAvailableVariant;
    });

    if (!variant && stock) stock.classList.add('hidden');
    if (product) product.classList.toggle('fine-art-product--ready', hasAvailableVariant);
  };

  const restoreInitialVariant = (picker, variants) => {
    const initialVariantId = picker.dataset.initialVariantId;
    if (!initialVariantId) {
      picker.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
        input.checked = false;
      });
      return null;
    }

    const initialVariant = variants.find((variant) => String(variant.id) === initialVariantId);
    if (!initialVariant) return null;

    const fieldsets = Array.from(picker.querySelectorAll('.fine-art-option-fieldset'));
    fieldsets.forEach((fieldset, index) => {
      const input = Array.from(fieldset.querySelectorAll('input[type="radio"]'))
        .find((radio) => radio.value === initialVariant.options[index]);
      if (input) input.checked = true;
    });
    return initialVariant;
  };

  const initPicker = (picker) => {
    if (picker.dataset.fineArtPickerReady === 'true') return;

    const variants = getVariantData(picker);
    const priceMap = getPriceMap(picker);
    const initialVariant = restoreInitialVariant(picker, variants);
    const styleFieldset = getFieldset(picker, 'frame-style');
    setMaterialState(picker, getCheckedValue(styleFieldset), Boolean(initialVariant));

    const sync = () => {
      const variant = getSelectedVariant(picker, variants);
      picker.currentVariant = variant || undefined;
      updatePurchaseState(picker, variant, priceMap);
    };

    picker.addEventListener('change', (event) => {
      if (!event.target.matches('input[type="radio"]')) return;
      const fieldset = event.target.closest('[data-option-role]');
      if (fieldset && fieldset.dataset.optionRole === 'frame-style') {
        setMaterialState(picker, event.target.value);
      }
    }, true);

    picker.addEventListener('change', sync);
    picker.dataset.fineArtPickerReady = 'true';
    sync();
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-fine-art-carousel]').forEach(initCarousel);
    root.querySelectorAll('.fine-art-variant-picker').forEach(initPicker);
  };

  const start = () => customElements.whenDefined('variant-radios').then(() => init());
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
